<template>
  <!--
    WP-03 -- turn bubble (student/tutor only; teacher role, citations,
    verdict, practice cards, follow-up suggestions are explicit non-scope,
    WP-04/05/07/09). Mirrors the rendered canonical prototype's
    `.message-row` / `.message-avatar` / `.message` pattern.

    WP-03-CHAT-CORRECTION-R3 (doc 11): a TUTOR bubble renders its content as
    Markdown + math (MarkdownContent -- markdown-it + KaTeX, raw HTML disabled), like the reference
    console; a STUDENT bubble stays the student's own literal text. One bubble
    serves the whole live reply: `loading` (nothing streamed yet) -> `streaming`
    (text grows in place) -> reconciled with the persisted turn.

    CHAT-UX-01 (doc 29 §0.3, §4.2) supersedes that last rule for PRESENTATION
    only: a student bubble now renders the student's text through the same
    safe renderer (`profile="student"`: Markdown and math, raw HTML still
    escaped, a ```figure fence stays code). The text itself -- what is stored,
    sent and edited -- is untouched. Nothing else of a tutor bubble applies
    to it (no reveal, no thinking state, no retry lead, no pages).

    WP-03-CHAT-VISUAL-PARITY (doc 11): the row is laid out with logical
    flex/border properties only. The student sits at the inline END and the
    tutor at the inline START in both directions (in RTL that is left and
    right respectively, exactly as in the rendered prototype). The previous
    `html.is-rtl` overrides re-flipped a layout that `direction: rtl` already
    flips and inverted the two sides -- they are gone.

    AI Tutor streaming presentation (doc 27, ST-02 / ST-03): while a reply is
    live the tutor avatar is the indicator (`liveStage`) -- a halo around a
    tile that never moves -- and the waiting state is `TutorThinking`. The
    text this bubble is given is already the VISIBLE text; it renders it as it
    renders any other.

    AT-API-020-H3 (doc 34/03 §7): a tutor reply's sources (Tutor API 0.2.0
    `citations`) are listed under its text by ChatSources, and its `[^n]`
    markers are small numbers (MarkdownContent). The 0.1 book-page images
    (`pageImageUrls`, the "trailing media") no longer exist: 0.2.0 never
    sends them.
  -->
  <div
    class="ai-tutor-turn"
    :class="[
      `ai-tutor-turn--${role}`,
      {
        'ai-tutor-turn--pending': loading,
        'ai-tutor-turn--live': !!liveStage,
        [`ai-tutor-turn--live-${liveStage}`]: !!liveStage,
        'ai-tutor-turn--reconnecting':
          loading && thinkingPhase === 'reconnecting',
      },
    ]"
    :data-testid="`ai-tutor-turn-${role}`"
  >
    <div
      class="ai-tutor-turn__avatar"
      :class="`ai-tutor-turn__avatar--${role}`"
    >
      <student-home-icon v-if="role === 'tutor'" name="spark" :size="18" />
      <template v-else>{{ initial }}</template>
    </div>
    <div
      ref="bubble"
      class="ai-tutor-turn__bubble"
      :class="`ai-tutor-turn__bubble--${role}`"
      :style="bubbleStyle"
      :aria-busy="liveStage ? 'true' : null"
    >
      <p class="ai-tutor-turn__name">{{ roleLabel }}</p>
      <!-- STUDENT CHAT V2 image: V2's `.message-media--image`, before the
           message text. `uploading` dims it under V2's ring spinner. -->
      <!-- WB-M2A: a board picture (`item.board`) carries the «از تخته»
           badge with the board glyph; a picture whose signed URL no longer
           loads (after the store's bounded re-signing) shows an unavailable
           state in the same frame, its caption and badge kept. -->
      <figure
        v-for="(item, index) in loading ? [] : media"
        :key="`media-${index}`"
        class="ai-tutor-turn__media"
        :class="{
          'ai-tutor-turn__media--uploading': item.uploading,
          'ai-tutor-turn__media--board': item.board,
          'ai-tutor-turn__media--unavailable': isBroken(item),
        }"
        data-testid="ai-tutor-turn-image"
        :data-board="item.board ? 'true' : null"
      >
        <img
          v-if="!isBroken(item)"
          :src="item.url"
          :alt="item.alt || $t('aiTutorSetup.chat.image.messageAlt')"
          @load="$emit('media-load')"
          @error="onMediaError(item)"
        />
        <span
          v-else
          class="ai-tutor-turn__media-unavailable"
          role="img"
          data-testid="ai-tutor-turn-image-unavailable"
          :aria-label="$t('aiTutorSetup.chat.image.unavailable')"
        >
          {{ $t('aiTutorSetup.chat.image.unavailable') }}
        </span>
        <span
          v-if="item.uploading"
          class="ai-tutor-turn__media-progress"
          role="status"
          data-testid="ai-tutor-turn-image-uploading"
          :aria-label="$t('aiTutorSetup.chat.sendingLabel')"
        >
          <span class="ai-tutor-turn__spinner" aria-hidden="true"></span>
        </span>
        <figcaption
          :data-testid="item.board ? 'ai-tutor-turn-board-badge' : null"
        >
          <board-icon v-if="item.board" name="board" :size="15" />
          <student-home-icon v-else name="camera" :size="15" />
          {{ item.caption }}
        </figcaption>
      </figure>
      <!-- The waiting state (doc 27, ST-02): one truthful status, inside the
           same bubble the answer will be written in. -->
      <tutor-thinking
        v-if="loading"
        :phase="thinkingPhase"
        :since="thinkingSince"
        :leaving="thinkingLeaving"
      />
      <slot v-else-if="$slots.default"></slot>
      <div
        v-else-if="role === 'tutor'"
        class="ai-tutor-turn__content ai-tutor-turn__content--markdown"
        :class="{ 'ai-tutor-turn__content--live': !!liveStage }"
        data-testid="ai-tutor-turn-content"
      >
        <!-- STUDENT CHAT V2: a «نفهمیدم» re-teach carries V2's
             `.answer-lead--retry` header. -->
        <div
          v-if="retryLead"
          class="ai-tutor-turn__lead"
          data-testid="ai-tutor-retry-lead"
        >
          <span>{{ $t('aiTutorSetup.chat.feedback.retryLeadLabel') }}</span>
          <strong>{{ $t('aiTutorSetup.chat.feedback.retryLeadTitle') }}</strong>
        </div>
        <!-- POST-UAT-UX / PUX-A: a marker with a page image asks the list to
             open that page. -->
        <markdown-content
          :source="content"
          :figures="figures"
          :sources="sources"
          @open-citation="$emit('open-citation', $event)"
        />
        <span
          v-if="streaming"
          class="ai-tutor-turn__dots ai-tutor-turn__dots--typing"
          data-testid="ai-tutor-typing-indicator"
          role="status"
          :aria-label="$t('aiTutorSetup.chat.writingLabel')"
          ><i></i><i></i><i></i
        ></span>
      </div>
      <div
        v-else-if="content"
        class="ai-tutor-turn__content ai-tutor-turn__content--markdown"
        data-testid="ai-tutor-turn-content"
      >
        <markdown-content :source="content" profile="student" />
      </div>
      <!-- AT-API-020-H3: the sources the reply rests on (Tutor API 0.2.0
           `citations`), listed under the reply once the turn is persisted --
           in the place the 0.1 book-page images had (`pageImageUrls`, which
           0.2.0 never sends; that surface is gone). -->
      <!-- Package 38 UX-2: an image-backed card asks the list to open its
           page in the same viewer as the markers. -->
      <chat-sources
        v-if="role === 'tutor' && !loading && sources.length"
        :sources="sources"
        @open-citation="$emit('open-citation', $event)"
      />
      <!-- STUDENT CHAT V2: an optimistic message that failed to send is
           never shown as sent -- V2's error-note colours plus a resend. -->
      <span
        v-if="deliveryState === 'failed'"
        class="ai-tutor-turn__meta ai-tutor-turn__meta--failed"
        role="alert"
        data-testid="ai-tutor-turn-failed"
      >
        {{ $t('aiTutorSetup.chat.sendFailedLabel') }}
        <button
          type="button"
          class="ai-tutor-turn__resend"
          data-testid="ai-tutor-turn-resend"
          :disabled="resendDisabled"
          @click="$emit('resend')"
        >
          {{ $t('aiTutorSetup.chat.resendButton') }}
        </button>
      </span>
      <span
        v-else-if="meta"
        class="ai-tutor-turn__meta"
        :title="metaTitle || null"
        >{{ meta }}</span
      >
    </div>
  </div>
</template>

<script>
import StudentHomeIcon from '@/components/StudentHome/StudentHomeIcon.vue'
import BoardIcon from '@/components/AiTutor/Board/BoardIcon.vue'
import MarkdownContent from './MarkdownContent.vue'
import TutorThinking from './TutorThinking.vue'
import ChatSources from './ChatSources.vue'

export default {
  name: 'TurnBubble',
  components: {
    StudentHomeIcon,
    BoardIcon,
    MarkdownContent,
    TutorThinking,
    ChatSources,
  },
  props: {
    /** @type {import('vue').PropOptions<'student'|'tutor'>} */
    role: {
      type: String,
      required: true,
      validator: value => ['student', 'tutor'].includes(value),
    },
    content: {
      type: String,
      default: '',
    },
    meta: {
      type: String,
      default: '',
    },
    // CHAT-UX-01: what the meta line is, for a pointer that rests on it
    // (a tutor reply's is the time the student waited for it).
    metaTitle: {
      type: String,
      default: '',
    },
    // The student's own first-name initial (the prototype's `.message-avatar
    // --student` shows the user's initial, not a role letter). Falls back to
    // the role label's first letter when no name is known.
    initialSource: {
      type: String,
      default: '',
    },
    // D4 (doc 11): the tutor's "processing" state -- a provisional tutor
    // bubble with a subtle loading treatment, shown before any text streams.
    loading: {
      type: Boolean,
      default: false,
    },
    // The live reply is still being written: a small typing indicator follows
    // the growing text, inside the same bubble.
    streaming: {
      type: Boolean,
      default: false,
    },
    // STUDENT CHAT V2: this tutor turn is a «نفهمیدم» re-teach.
    retryLead: {
      type: Boolean,
      default: false,
    },
    // STUDENT CHAT V2: '' for a persisted turn, 'failed' for an optimistic
    // message that did not send (the only non-persisted state with its own
    // treatment; 'sending' is carried by `meta`).
    deliveryState: {
      type: String,
      default: '',
    },
    resendDisabled: {
      type: Boolean,
      default: false,
    },
    // STUDENT CHAT V2 image: [{url, caption, uploading}] shown above the text
    // (the persisted turn's signed `attachmentUrls`, or the optimistic
    // message's local preview).
    media: {
      type: Array,
      default: () => [],
    },
    // AT-API-020-H3: the tutor reply's sources (the `citations` of a 0.2.0
    // reply as the adapter maps them, [{n, title, page, item}]), listed under
    // the reply and named in its markers' titles.
    sources: {
      type: Array,
      default: () => [],
    },
    // FINAL CHAT V2: the turn's stored figure specs (MarkdownContent).
    figures: {
      type: Array,
      default: () => [],
    },
    // Doc 27 (ST-02): '' for a settled turn; 'thinking' while the reply is
    // awaited, 'writing' while it is being revealed. Drives the avatar only.
    liveStage: {
      type: String,
      default: '',
      validator: value => ['', 'thinking', 'writing'].includes(value),
    },
    // Doc 27 (ST-02): what the waiting state truthfully says
    // (thinkingPhase.js), since when the student has been waiting
    // (milliseconds on the presentation clock, 0 = unknown), and whether the
    // status is giving way to the first word.
    thinkingPhase: {
      type: String,
      default: 'question',
    },
    thinkingSince: {
      type: Number,
      default: 0,
    },
    thinkingLeaving: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // The widest the live bubble has been: it never shrinks under a shorter
      // status or under the first words of the answer.
      heldWidth: 0,
      // WB-M2A: the picture URLs that failed to load. A re-signed URL (the
      // store refetches on a failure) is a new URL and a fresh attempt.
      brokenMedia: {},
    }
  },
  computed: {
    bubbleStyle() {
      // never wider than the row itself (a rotated phone)
      return this.heldWidth
        ? { minWidth: `min(${this.heldWidth}px, 100%)` }
        : null
    },
    roleLabel() {
      return this.role === 'student'
        ? this.$t('aiTutorSetup.chat.studentLabel')
        : this.$t('aiTutorSetup.chat.tutorLabel')
    },
    initial() {
      const source = (this.initialSource || '').trim() || this.roleLabel
      return Array.from(source)[0] || ''
    },
  },
  watch: {
    // Both run before the DOM changes, so this is still the width of what
    // is about to be replaced.
    loading(loading, was) {
      if (was && !loading) this.holdWidth()
    },
    thinkingPhase() {
      if (this.loading) this.holdWidth()
    },
  },
  methods: {
    isBroken(item) {
      return !!item && !!item.url && this.brokenMedia[item.url] === true
    },
    onMediaError(item) {
      if (item && item.url) this.$set(this.brokenMedia, item.url, true)
      this.$emit('media-error', item)
    },
    holdWidth() {
      const bubble = this.$refs.bubble
      // the exact width, rounded up: the held bubble is never narrower
      const width =
        bubble && typeof bubble.getBoundingClientRect === 'function'
          ? Math.ceil(bubble.getBoundingClientRect().width || 0)
          : 0
      if (width > this.heldWidth) this.heldWidth = width
    },
  },
}
</script>

<style lang="scss" scoped>
// Exact px values measured from the rendered canonical prototype
// (`.message-row`, `.message-avatar`, `.message`; sha256 75678bee...) --
// D1 (doc 11): the chat UI uses the prototype's own type scale. Line-height
// is unitless and inherited from the chat wrapper (1.75), except the bubble
// itself (1.9), as in the prototype.
$ai-tutor-ink: $borna-ink;
$ai-tutor-text: $borna-text;
$ai-tutor-indigo: $borna-indigo-600;
$ai-tutor-indigo-50: $borna-indigo-50;
$ai-tutor-indigo-100: $borna-indigo-100;
$ai-tutor-muted: $borna-muted;
$ai-tutor-muted-light: $borna-muted-light;
$ai-tutor-border: $borna-border;

.ai-tutor-turn {
  display: flex;
  align-items: flex-start;
  gap: 9px;

  &--student {
    flex-direction: row-reverse;
  }
}

.ai-tutor-turn__avatar {
  position: relative;
  display: grid;
  place-items: center;
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  border-radius: 11px;
  color: #fff;
  font-size: 11px;
  font-weight: 800;

  &--student {
    background: $ai-tutor-ink;
  }

  &--tutor {
    background: $ai-tutor-indigo;
  }
}

.ai-tutor-turn__bubble {
  min-width: 0;
  max-width: min(84%, 680px);
  padding: 15px 17px;
  border: 1px solid $ai-tutor-border;
  border-radius: 18px;
  background: #fff;
  color: $ai-tutor-text;
  font-size: 12px;
  line-height: 1.9;
  // A long unbroken token (URL, formula) wraps instead of widening the bubble
  // past the column.
  overflow-wrap: anywhere;

  // The single small corner sits at the top, on the side facing away from
  // the avatar (prototype: student 18/5/18/18, tutor 5/18/18 in RTL).
  &--student {
    border-color: $ai-tutor-indigo-100;
    background: $ai-tutor-indigo-50;
    border-start-start-radius: 5px;
  }

  &--tutor {
    border-start-end-radius: 5px;
  }
}

.ai-tutor-turn__name {
  margin: 0 0 7px;
  color: $ai-tutor-muted;
  font-size: 10px;
  font-weight: 700;
  line-height: 19px;
}

.ai-tutor-turn__content {
  margin: 0 0 8px;
  overflow-wrap: break-word;
  white-space: pre-line;
}

// Markdown reply: block spacing comes from MarkdownContent, and newlines are
// already turned into line breaks by the renderer (no `pre-line` here).
.ai-tutor-turn__content--markdown {
  margin: 0 0 8px;
  white-space: normal;
}

// ---------------------------------------------------------------------------
// AI Tutor streaming presentation (doc 27, ST-02). Measured from the rendered
// design candidate (`.st-live`, sha256 03529aea...6ed5) at 1440 and 390.
//
// The avatar is the live indicator: a soft halo breathes behind it and a
// highlight travels round its edge. The tile and its icon do not move
// (doc 27 §28.2 D1).
// ---------------------------------------------------------------------------
.ai-tutor-turn--live {
  // the halo sits behind the avatar, inside this row only
  isolation: isolate;

  .ai-tutor-turn__avatar--tutor::before,
  .ai-tutor-turn__avatar--tutor::after {
    content: '';
    position: absolute;
    border-radius: 14px;
    pointer-events: none;
  }

  .ai-tutor-turn__avatar--tutor::before {
    top: -4px;
    right: -4px;
    bottom: -4px;
    left: -4px;
    z-index: -1;
    background: $ai-tutor-indigo;
    opacity: 0;
  }

  .ai-tutor-turn__avatar--tutor::after {
    top: -3px;
    right: -3px;
    bottom: -3px;
    left: -3px;
    padding: 2px;
    background: conic-gradient(
      from var(--ai-tutor-sweep, 0deg),
      rgba(49, 75, 214, 0) 0deg,
      rgba(49, 75, 214, 0) 200deg,
      rgba(145, 165, 255, 0.95) 320deg,
      rgba(49, 75, 214, 0) 360deg
    );
    -webkit-mask: linear-gradient(#000 0 0) content-box,
      linear-gradient(#000 0 0);
    -webkit-mask-composite: xor;
    mask: linear-gradient(#000 0 0) content-box exclude,
      linear-gradient(#000 0 0);
    opacity: 0;
  }
}

.ai-tutor-turn--live-thinking {
  .ai-tutor-turn__avatar--tutor::before {
    animation: ai-tutor-turn-halo 2.4s ease-in-out infinite;
  }

  .ai-tutor-turn__avatar--tutor::after {
    opacity: 1;
    animation: ai-tutor-turn-sweep 1.8s linear infinite;
  }
}

// While the answer is being written: the halo alone, slower and fainter.
.ai-tutor-turn--live-writing .ai-tutor-turn__avatar--tutor::before {
  animation: ai-tutor-turn-halo-soft 3.2s ease-in-out infinite;
}

.ai-tutor-turn--reconnecting {
  .ai-tutor-turn__avatar--tutor {
    opacity: 0.55;
    transition: opacity 0.3s ease;
  }

  .ai-tutor-turn__avatar--tutor::before,
  .ai-tutor-turn__avatar--tutor::after {
    opacity: 0;
    animation: none;
  }
}

// A first line of answer plus the typing dots is as tall as the status it
// replaces, so the hand-off changes nothing about the bubble but its content.
.ai-tutor-turn__content--live {
  min-height: 48px;
}

@property --ai-tutor-sweep {
  syntax: '<angle>';
  inherits: false;
  initial-value: 0deg;
}

@keyframes ai-tutor-turn-halo {
  0%,
  100% {
    opacity: 0.1;
    transform: scale(0.92);
  }
  50% {
    opacity: 0.22;
    transform: scale(1.12);
  }
}

@keyframes ai-tutor-turn-halo-soft {
  0%,
  100% {
    opacity: 0.05;
    transform: scale(0.95);
  }
  50% {
    opacity: 0.13;
    transform: scale(1.08);
  }
}

@keyframes ai-tutor-turn-sweep {
  to {
    --ai-tutor-sweep: 360deg;
  }
}

// STUDENT CHAT V2 -- V2 `.voice-spinner`: 24px ring, 3px indigo-100 track
// with an indigo-600 head, .75s linear (the photo being uploaded).
.ai-tutor-turn__spinner {
  flex: none;
  box-sizing: border-box;
  width: 24px;
  height: 24px;
  border: 3px solid $ai-tutor-indigo-100;
  border-top-color: $ai-tutor-indigo;
  border-radius: 50%;
  animation: ai-tutor-turn-spin 0.75s linear infinite;
}

// STUDENT CHAT V2 -- V2 `.answer-lead` + `.answer-lead--retry`.
.ai-tutor-turn__lead {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin: -3px -4px 13px;
  padding: 12px;
  border: 1px solid $borna-teal-100;
  border-radius: 12px;
  background: linear-gradient(135deg, $borna-teal-50, #fff);
  color: $borna-teal-700;
  line-height: 1.75;

  span {
    font-size: 9px;
  }

  strong {
    font-size: 15px;
  }
}

// STUDENT CHAT V2 image -- V2 `.message-media` / `.message-media--image`:
// 5px/8px vertical margin, 14px radius, translucent white frame; the photo
// fills min(100%, 360px) up to 260px tall (100% / 210px from 620px down); a
// 12px caption row. While uploading, the photo dims under V2's ring spinner.
.ai-tutor-turn__media {
  position: relative;
  margin: 5px 0 8px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.26);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.08);

  img {
    display: block;
    width: min(100%, 360px);
    max-height: 260px;
    object-fit: cover;
  }

  figcaption {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 7px 9px;
    font-size: 12px;
  }

  &--uploading img {
    opacity: 0.55;
  }

  // WB-M2A: a board picture is drawn whole on its white paper (never
  // cropped: what the Student saw in the sheet is what is shown here).
  &--board img {
    object-fit: contain;
    background: #fff;
  }

  &--board figcaption {
    color: $borna-indigo-900;
    font-weight: 700;
  }
}

.ai-tutor-turn__media-progress {
  position: absolute;
  inset: 0 0 auto;
  display: grid;
  place-items: center;
  height: calc(100% - 36px);
}

// WB-M2A: the picture's frame when the picture itself is gone.
.ai-tutor-turn__media-unavailable {
  display: grid;
  place-items: center;
  box-sizing: border-box;
  width: min(100%, 360px);
  min-height: 96px;
  padding: 12px;
  background: $borna-canvas;
  color: $ai-tutor-muted;
  font-size: 11px;
  text-align: center;
}

.ai-tutor-turn__meta--failed {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  color: $borna-error-text;
}

.ai-tutor-turn__resend {
  padding: 0;
  border: 0;
  background: none;
  color: $ai-tutor-indigo;
  font: inherit;
  font-weight: 800;
  text-decoration: underline;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.ai-tutor-turn__dots {
  display: inline-flex;
  gap: 4px;

  i {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: $ai-tutor-indigo;
    opacity: 0.35;
    animation: ai-tutor-turn-dot 1.1s ease-in-out infinite;

    &:nth-child(2) {
      animation-delay: 0.15s;
    }

    &:nth-child(3) {
      animation-delay: 0.3s;
    }
  }
}

.ai-tutor-turn__dots--typing {
  margin-top: 6px;
}

.ai-tutor-turn__meta {
  display: block;
  margin-top: 7px;
  color: $ai-tutor-muted-light;
  font-size: 10px;
  line-height: 19px;
}

// A bubble that ends with its own content should not carry the paragraph's
// trailing gap.
.ai-tutor-turn__bubble > :last-child {
  margin-bottom: 0;
}

@keyframes ai-tutor-turn-dot {
  0%,
  100% {
    opacity: 0.35;
  }
  40% {
    opacity: 1;
  }
}

@keyframes ai-tutor-turn-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .ai-tutor-turn__dots i {
    animation: none;
    opacity: 0.7;
  }

  .ai-tutor-turn__spinner {
    animation: none;
  }

  // The status stays; the decoration stops.
  .ai-tutor-turn--live .ai-tutor-turn__avatar--tutor::before,
  .ai-tutor-turn--live .ai-tutor-turn__avatar--tutor::after {
    opacity: 0;
    animation: none;
  }

  .ai-tutor-turn--reconnecting .ai-tutor-turn__avatar--tutor {
    transition: none;
  }
}

// The prototype's <=620px rule.
@media (max-width: 620px) {
  .ai-tutor-turn__bubble {
    max-width: calc(100% - 40px);
  }

  .ai-tutor-turn__media img {
    width: 100%;
    max-height: 210px;
  }
}
</style>
